import { useMemo, useState } from 'react'
import { App, Button, Select, Space, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CheckOutlined, CloseOutlined, SendOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { Can } from '@/shared/auth/Can'
import { useAuth } from '@/shared/auth/useAuth'
import { formatDateTime } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import { StockMovementModal } from '@/features/inventory/StockMovementModal'
import { ReturnRejectModal } from '@/features/inventory/ReturnRejectModal'
import { TransferReceiveModal } from '@/features/inventory/TransferReceiveModal'
import {
  TRANSFER_STATES,
  fetchStockItems,
  fetchTransferPage,
  fetchWarehouses,
  type StockTransfer,
  type TransferState,
} from '@/features/inventory/stockApi'

const STATE_COLOR: Record<TransferState, string> = { IN_TRANSIT: 'gold', RECEIVED: 'green', REJECTED: 'red' }

export function StockTransfersPage() {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { user } = useAuth()
  const actors = useActorDirectory()
  const [state, setState] = useState<TransferState | null>('IN_TRANSIT')
  const [nodeId, setNodeId] = useState<string | null>(null)
  const [dispatching, setDispatching] = useState(false)
  const [receiving, setReceiving] = useState<StockTransfer | null>(null)
  const [rejecting, setRejecting] = useState<StockTransfer | null>(null)

  const warehouses = useQuery({ queryKey: queryKeys.stock.warehouses, queryFn: fetchWarehouses })
  const items = useQuery({ queryKey: queryKeys.stock.items(true), queryFn: () => fetchStockItems(true), enabled: dispatching })
  const filter = useMemo(() => ({ state, nodeId }), [state, nodeId])
  const fetchPage = useMemo(() => fetchTransferPage(filter), [filter])

  const columns = useMemo<ColumnsType<StockTransfer>>(
    () => [
      { key: 'dispatchedAt', title: t('stockTransfer.field.dispatchedAt'), width: 170, render: (_, row) => formatDateTime(row.dispatchedAt) },
      { key: 'item', title: t('stockTransfer.field.item'), width: 220, render: (_, row) => `${row.itemName} · ${row.sku}` },
      { key: 'route', title: t('stockTransfer.field.route'), width: 260, render: (_, row) => `${row.fromNodeName} → ${row.toNodeName}` },
      {
        key: 'direction',
        title: t('stockTransfer.field.direction'),
        width: 220,
        render: (_, row) => (
          <Tag color={row.direction === 'RETURN' ? 'purple' : 'blue'}>{t(`stockTransfer.direction.${row.direction}`)}</Tag>
        ),
      },
      {
        key: 'state',
        title: t('stockTransfer.field.state'),
        width: 140,
        render: (_, row) => (
          <Tag color={STATE_COLOR[row.state]}>{t(`stockTransfer.state.${row.state}`)}</Tag>
        ),
      },
      { key: 'sent', title: t('stockTransfer.field.sent'), width: 110, align: 'right', render: (_, row) => `${row.quantitySent} ${row.unit}` },
      {
        key: 'received',
        title: t('stockTransfer.field.received'),
        width: 110,
        align: 'right',
        render: (_, row) => (row.quantityReceived === null ? t('common.dash') : `${row.quantityReceived} ${row.unit}`),
      },
      {
        key: 'shortage',
        title: t('stockTransfer.field.shortage'),
        width: 170,
        render: (_, row) =>
          row.quantityReceived === null || row.quantityDamaged === null
            ? t('common.dash')
            : t('stockTransfer.shortageValue', {
                damaged: row.quantityDamaged,
                missing: row.quantitySent - row.quantityReceived - row.quantityDamaged,
              }),
      },
      { key: 'cost', title: t('stockTransfer.field.cost'), width: 140, align: 'right', render: (_, row) => formatMoneyValue(row.cost) },
      {
        key: 'price',
        title: t('stockTransfer.field.internalPrice'),
        width: 150,
        align: 'right',
        render: (_, row) => (row.direction === 'SUPPLY' ? formatMoneyValue(row.inNodeCost) : t('common.dash')),
      },
      {
        key: 'returnInfo',
        title: t('stockTransfer.field.returnInfo'),
        width: 240,
        render: (_, row) =>
          row.direction !== 'RETURN'
            ? t('common.dash')
            : row.state === 'REJECTED'
              ? t('stockTransfer.rejectedInfo', { reason: row.returnReason, note: row.rejectNote })
              : `${row.returnReason} · ${row.returnCondition}`,
      },
      { key: 'dispatchedBy', title: t('stockTransfer.field.dispatchedBy'), width: 150, render: (_, row) => actorLabel(row.dispatchedBy, actors, t('common.dash')) },
      {
        key: 'receivedBy',
        title: t('stockTransfer.field.receivedBy'),
        width: 220,
        render: (_, row) =>
          row.receivedBy
            ? `${actorLabel(row.receivedBy, actors, t('common.dash'))} · ${formatDateTime(row.receivedAt)}`
            : t('common.dash'),
      },
      { key: 'docNo', title: t('stockTransfer.field.docNo'), width: 120, render: (_, row) => row.docNo ?? t('common.dash') },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          <RowActions
            items={[
              {
                key: 'receive',
                label: t('stockTransfer.receive.open'),
                icon: <CheckOutlined />,
                permission: 'inventory.transfer.receive',
                hidden: row.state !== 'IN_TRANSIT' || row.direction === 'RETURN' || row.dispatchedBy === user?.username,
                onClick: () => setReceiving(row),
              },
              {
                key: 'acceptReturn',
                label: t('stockTransfer.returnAccept.open'),
                icon: <CheckOutlined />,
                permission: 'inventory.return.accept',
                hidden: row.state !== 'IN_TRANSIT' || row.direction !== 'RETURN' || row.dispatchedBy === user?.username,
                onClick: () => setReceiving(row),
              },
              {
                key: 'rejectReturn',
                label: t('stockTransfer.returnReject.open'),
                icon: <CloseOutlined />,
                danger: true,
                permission: 'inventory.return.accept',
                hidden: row.state !== 'IN_TRANSIT' || row.direction !== 'RETURN' || row.dispatchedBy === user?.username,
                onClick: () => setRejecting(row),
              },
              {
                key: 'selfHint',
                label: t('stockTransfer.selfReceiveHint'),
                disabled: true,
                hidden: row.state !== 'IN_TRANSIT' || row.dispatchedBy !== user?.username,
                onClick: () => undefined,
              },
            ]}
          />
        ),
      },
    ],
    [t, user?.username, actors],
  )

  return (
    <>
      <PageHeader
        title={t('nav.stockTransfers')}
        description={t('pageIntro.stockTransfers')}
        extra={
          <Can permission="inventory.transfer.dispatch">
            <Button type="primary" icon={<SendOutlined />} onClick={() => setDispatching(true)}>
              {t('stockTransfer.dispatch')}
            </Button>
          </Can>
        }
      />
      <Space wrap style={{ marginBottom: 16 }}>
        <Select<TransferState | 'ALL'>
          style={{ minWidth: 180 }}
          value={state ?? 'ALL'}
          onChange={(value) => setState(value === 'ALL' ? null : value)}
          options={[
            { value: 'ALL', label: t('stockTransfer.allStates') },
            ...TRANSFER_STATES.map((value) => ({ value, label: t(`stockTransfer.state.${value}`) })),
          ]}
        />
        <Select<string>
          allowClear
          style={{ minWidth: 220 }}
          placeholder={t('stockTransfer.allWarehouses')}
          value={nodeId ?? undefined}
          onChange={(value) => setNodeId(value ?? null)}
          options={(warehouses.data ?? []).map((w) => ({ value: w.id, label: `${w.displayName} (${w.code})` }))}
        />
      </Space>
      <DataTable<StockTransfer>
        queryKey={[...queryKeys.stock.transfers, filter]}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="stock-transfers"
        scrollX={2620}
        stickyFirstColumn
        emptyTitle={t('stockTransfer.empty')}
      />
      <StockMovementModal
        open={dispatching}
        action="TRANSFER"
        warehouses={warehouses.data ?? []}
        items={items.data ?? []}
        onClose={() => setDispatching(false)}
        onDone={(result) => {
          if (result.kind !== 'transfer') return
          message.success(
            t('stockTransfer.dispatched', {
              quantity: result.transfer.quantitySent,
              unit: result.transfer.unit,
              to: result.transfer.toNodeName,
            }),
          )
        }}
      />
      <TransferReceiveModal transfer={receiving} onClose={() => setReceiving(null)} />
      <ReturnRejectModal transfer={rejecting} onClose={() => setRejecting(null)} />
    </>
  )
}
